import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { GlobalLoaderService } from './global-loader.service';
import { GlobalLoaderState } from '../models/bulk-search.models';
import { BulkSearchService } from '../../services/bulk-search.service';
import { Router, ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  GenericTemplateCommonService
} from '../components/template-ai-common/services/generic-template-common.service';

@Component({
  selector: 'app-global-loader',
  standalone: true,
  imports: [MatProgressSpinnerModule, MatProgressBarModule, MatButtonModule, MatIconModule, CommonModule],
  templateUrl: './global-loader.component.html',
  styleUrl: './global-loader.component.scss'
})
export class GlobalLoaderComponent implements OnInit, OnDestroy, OnChanges {
  /** Must match UNIFIED_BULK_TOTAL_TABS in generic-template-common (phase 1 + phase 2). */
  private static readonly UNIFIED_BULK_STEP_COUNT = 14;

  // Input properties for title and custom loader type
  @Input() title: string = 'Please wait...';
  @Input() isCustomLoader: boolean = false;
  @Input() showBackgroundButton: boolean = false;
  @Input() showSkipAuditsButton: boolean = false;
  @Input() currentTaskId: string | null = null;
  @Input() projectId: string | null = null;
  
  // Output events
  @Output() backgroundLoadClick = new EventEmitter<void>();
  @Output() skipAuditsClick = new EventEmitter<void>();
  @Output() cancelClick = new EventEmitter<void>();
  
  showBackgroundLoadButton = false;
  auditsSkipped = false;
  showCancelButton = false;
  private backgroundButtonTimer?: any;
  private cancelButtonTimer?: any;

  // Current loader state from service (for progress tracking)
  loaderState: GlobalLoaderState = {
    showProgress: false,
    progressPercentage: 0,
    completedTabs: 0,
    totalTabs: 0,
    completedTabNames: [],
    currentTab: '',
    estimatedTimeRemaining: null,
    overviewStagesMode: false,
    overviewBackendStage: null,
    servedFromCache: false
  };

  private subscription: Subscription = new Subscription();

  constructor(
    private globalLoaderService: GlobalLoaderService,
    private bulkSearchService: BulkSearchService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private genericTemplateCommonService: GenericTemplateCommonService
  ) {}

  private static readonly BODY_SCROLL_LOCK_CLASS = 'global-loader-active';

  ngOnInit(): void {
    // Prevent background scroll while loader is visible
    if (typeof document !== 'undefined') {
      document.body.classList.add(GlobalLoaderComponent.BODY_SCROLL_LOCK_CLASS);
    }
    // Subscribe to loader state changes for progress updates
    this.subscription.add(
      this.globalLoaderService.loaderState$.subscribe(state => {
        this.loaderState = state;
        // 7 analysis tabs; audits are not included (use Re-Audit per tab)
        if (state.totalTabs === 7 && state.showProgress) {
          this.auditsSkipped = true;
        }
      })
    );
    
    // Show background load button after 2-3 seconds delay (for both progress and standard mode)
    if (this.showBackgroundButton) {
      this.backgroundButtonTimer = setTimeout(() => {
        this.showBackgroundLoadButton = true;
      }, 2500); // 2.5 seconds delay
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Set cancel button timer when currentTaskId becomes available
    if (changes['currentTaskId']) {
      if (this.currentTaskId && !this.cancelButtonTimer) {
        this.cancelButtonTimer = setTimeout(() => {
          this.showCancelButton = true;
        }, 900000); // 15 minutes
      } else if (!this.currentTaskId && this.cancelButtonTimer) {
        // Clear timer if currentTaskId becomes null
        clearTimeout(this.cancelButtonTimer);
        this.cancelButtonTimer = undefined;
        this.showCancelButton = false;
      }
    }
  }

  ngOnDestroy(): void {
    if (typeof document !== 'undefined') {
      document.body.classList.remove(GlobalLoaderComponent.BODY_SCROLL_LOCK_CLASS);
    }
    this.subscription.unsubscribe();
    if (this.backgroundButtonTimer) {
      clearTimeout(this.backgroundButtonTimer);
    }
    if (this.cancelButtonTimer) {
      clearTimeout(this.cancelButtonTimer);
    }
  }
  
  onBackgroundLoadClick(): void {
    this.backgroundLoadClick.emit();
  }
  
  onSkipAuditsClick(): void {
    this.skipAuditsClick.emit();
  }
  
  onCancelClick(): void {
    this.cancelClick.emit();
  }

  // Getters for template compatibility (progress-related properties from service)
  get showProgress(): boolean { return this.loaderState.showProgress; }
  get progressPercentage(): number { return this.loaderState.progressPercentage; }
  get completedTabs(): number { return this.loaderState.completedTabs; }
  get totalTabs(): number { return this.loaderState.totalTabs; }
  get completedTabNames(): string[] { 
    let names = this.loaderState.completedTabNames;
    // Filter out audit tabs if audits are skipped
    if (this.auditsSkipped) {
      const auditTabs = ['EEAT Audit', 'Landing Page Audit', 'AI Audit'];
      names = names.filter(tab => !auditTabs.includes(tab));
    }
    return names.map(tab => this.getDisplayTabName(tab)); 
  }
  /** Display label for tab name in loader (e.g. "overview-prompts-build" -> "Overview") */
  getDisplayTabName(tab: string): string {
    if (!tab) return tab;
    const t = tab.toLowerCase();
    if (t === 'overview-prompts-build') return 'Overview';
    if (t === 'category-analysis') return 'Category Analysis';
    if (t === 'ai-visibility') return 'AI Visibility';
    if (t === 'ai-citations') return 'AI Citations';
    if (t === 'consumer-insights') return 'Consumer Insights';
    if (t === 'sentiment-tracking') return 'Sentiment Tracking';
    if (t === 'ai-content-index') return 'AI Content Index';
    if (t === 'content-creation') return 'Content Creation';
    if (t === 'performance-tracking') return 'Performance Tracking';
    if (t === 'prompt-creation') return 'Prompt Creation';
    // Humanise other keys: "some-tab" -> "Some tab"
    return tab.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  get currentTab(): string { 
    const current = this.loaderState.currentTab;
    const normalized = (current || '').toLowerCase();
    if (normalized.includes('undefined/undefined')) {
      return '';
    }
    // Hide audit tabs from current tab display if audits are skipped
    if (this.auditsSkipped) {
      const auditTabs = ['eeat-audit', 'landing-page-audit', 'ai-audit', 'EEAT Audit', 'Landing Page Audit', 'AI Audit'];
      if (auditTabs.some(audit => current.toLowerCase().includes(audit.toLowerCase()))) {
        return '';
      }
    }
    return this.getDisplayTabName(current); 
  }
  get estimatedTimeRemaining(): number | null { return this.loaderState.estimatedTimeRemaining; }
  get elapsedTimeSeconds(): number | null { return this.loaderState.elapsedTimeSeconds ?? null; }
  get servedFromCache(): boolean { return !!this.loaderState.servedFromCache; }

  /** Formatted elapsed time e.g. "1m 30s" for progress bar */
  getElapsedTimeText(): string {
    const sec = this.elapsedTimeSeconds;
    if (sec == null || sec < 0) return '';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  }
  
  /**
   * Detect the audit type from current route, tab, or context
   * ROOT CAUSE FIX: Uses the actual active tab from GenericTemplateCommonService
   */
  private getAuditType(): string {
    // Priority 1: Get active tab from GenericTemplateCommonService (ROOT CAUSE - most reliable)
    // This is the actual source of truth for which tab is currently active
    try {
      const currentTabFlow = this.genericTemplateCommonService.getCurrentTabFlow();
      const activeTab = currentTabFlow?.activeTab;
      
      if (activeTab) {
        const lowerTab = activeTab.toLowerCase();
        
        // Check for EEAT audit
        if (lowerTab === 'eeat-audit' || lowerTab.includes('eeat')) {
          return 'eeat-audit';
        }
        
        // Check for Landing Page audit
        if (lowerTab === 'landing-page-audit' || 
            (lowerTab.includes('landing') && lowerTab.includes('page'))) {
          return 'landing-page-audit';
        }
        
        // Check for AI audit
        if (lowerTab === 'ai-audit' || 
            (lowerTab.includes('ai') && lowerTab.includes('audit') && !lowerTab.includes('landing'))) {
          return 'ai-audit';
        }
      }
    } catch (error) {
      console.warn('Could not get active tab from GenericTemplateCommonService:', error);
    }
    
    // Priority 2: Check loaderState currentTab (for tabs being processed)
    if (this.loaderState.currentTab) {
      const lowerTab = this.loaderState.currentTab.toLowerCase();
      
      if (lowerTab.includes('eeat-audit') || lowerTab.includes('eeat audit') || 
          (lowerTab.includes('eeat') && lowerTab.includes('audit'))) {
        return 'eeat-audit';
      }
      if (lowerTab.includes('landing-page-audit') || lowerTab.includes('landing page audit') ||
          (lowerTab.includes('landing') && lowerTab.includes('page') && lowerTab.includes('audit'))) {
        return 'landing-page-audit';
      }
      if (lowerTab.includes('ai-audit') || lowerTab.includes('ai audit') ||
          (lowerTab.includes('ai') && lowerTab.includes('audit') && !lowerTab.includes('landing'))) {
        return 'ai-audit';
      }
    }
    
    // Priority 3: Check currentTab property (filtered version)
    if (this.currentTab) {
      const lowerTab = this.currentTab.toLowerCase();
      
      if (lowerTab.includes('eeat-audit') || lowerTab.includes('eeat audit') || 
          (lowerTab.includes('eeat') && lowerTab.includes('audit'))) {
        return 'eeat-audit';
      }
      if (lowerTab.includes('landing-page-audit') || lowerTab.includes('landing page audit') ||
          (lowerTab.includes('landing') && lowerTab.includes('page') && lowerTab.includes('audit'))) {
        return 'landing-page-audit';
      }
      if (lowerTab.includes('ai-audit') || lowerTab.includes('ai audit') ||
          (lowerTab.includes('ai') && lowerTab.includes('audit') && !lowerTab.includes('landing'))) {
        return 'ai-audit';
      }
    }
    
    // Priority 4: Check completed tabs (in case we're processing an audit tab)
    if (this.loaderState.completedTabNames && this.loaderState.completedTabNames.length > 0) {
      const allCompletedTabs = this.loaderState.completedTabNames.join(' ').toLowerCase();
      
      if (allCompletedTabs.includes('eeat-audit') || allCompletedTabs.includes('eeat audit') || 
          (allCompletedTabs.includes('eeat') && allCompletedTabs.includes('audit'))) {
        return 'eeat-audit';
      }
      if (allCompletedTabs.includes('landing-page-audit') || allCompletedTabs.includes('landing page audit') ||
          (allCompletedTabs.includes('landing') && allCompletedTabs.includes('page') && allCompletedTabs.includes('audit'))) {
        return 'landing-page-audit';
      }
      if (allCompletedTabs.includes('ai-audit') || allCompletedTabs.includes('ai audit') ||
          (allCompletedTabs.includes('ai') && allCompletedTabs.includes('audit') && !allCompletedTabs.includes('landing'))) {
        return 'ai-audit';
      }
    }
    
    return 'default';
  }

  getEncouragementMessage(): string {
    if (this.showProgress) {
      if (this.progressPercentage < 20) {
        // Use switch case to determine the appropriate message based on audit type
        const auditType = this.getAuditType();
        switch (auditType) {
          case 'eeat-audit':
            return "🚀 Starting your comprehensive EEAT audit analysis...";
          case 'landing-page-audit':
            return "🚀 Starting your comprehensive landing page audit analysis...";
          case 'ai-audit':
            return "🚀 Starting your comprehensive AI audit analysis...";
          default:
            // Default to landing page analysis for bulk search
            return "🚀 Starting your comprehensive brand analysis...";
        }
      } else if (this.progressPercentage < 40) {
        return "📊 Processing data and generating insights...";
      } else if (this.progressPercentage < 60) {
        return "🎯 Analyzing market trends and competitor data...";
      } else if (this.progressPercentage < 80) {
        return "📈 Creating detailed charts and visualizations...";
      } else if (this.progressPercentage < 95) {
        return "✨ Almost there! Finalizing your dashboard...";
      } else {
        return "🎉 Just a moment more! Your results are almost ready!";
      }
    }
    
    // Default messages for non-progress loaders
    const messages = [
      "We're preparing your dashboard. This may take a few moments as we process and analyze your data using advanced AI computations. Thank you for your patience—your results will be ready soon!",
      "☕ Relax and take a coffee break! We're analyzing your data and creating comprehensive charts across multiple tabs. This will only take a few minutes.",
      "🚀 Our AI is working hard to provide you with the most accurate and comprehensive analysis. Great things take time!",
      "📊 We're building your personalized dashboard with real-time data analysis. Almost there!"
    ];
    
    return messages[Math.floor(Math.random() * messages.length)];
  }
  
  getTimeRemainingText(): string {
    if (this.estimatedTimeRemaining && this.estimatedTimeRemaining > 0) {
      const minutes = Math.floor(this.estimatedTimeRemaining / 60);
      const seconds = Math.floor(this.estimatedTimeRemaining % 60);
      if (minutes > 0) {
        return `Estimated time remaining: ${minutes}m ${seconds}s`;
      }
      return `Estimated time remaining: ${seconds}s`;
    }
    return '';
  }

  /** Remaining time from backend/WebSocket; until then, show a minute-range hint for single-tab audits or bulk. */
  getEstimatedOrRemainingText(): string {
    if (this.estimatedTimeRemaining != null && this.estimatedTimeRemaining > 0) {
      const minutes = Math.floor(this.estimatedTimeRemaining / 60);
      const seconds = Math.floor(this.estimatedTimeRemaining % 60);
      const rawTab = (this.loaderState.currentTab || '').toLowerCase();
      const queueHint =
        rawTab.includes('queue') ||
        rawTab.includes('worker') ||
        rawTab.includes('position');
      if (queueHint) {
        if (minutes > 0) {
          return `Estimated start in ~${minutes}m ${seconds}s`;
        }
        return `Estimated start in ~${seconds}s`;
      }
      if (minutes > 0) {
        return `Remaining: ~${minutes}m ${seconds}s`;
      }
      return `Remaining: ~${seconds}s`;
    }
    if (this.showProgress) {
      if (this.totalTabs === 1) {
        return this.getTypicalDurationHintForSingleTabAudit();
      }
      return 'Full analysis time varies; ETA updates as each step completes';
    }
    return '';
  }

  /** Shown before first server ETA when running a single-tab re-audit (EEAT / Landing / AI). */
  private getTypicalDurationHintForSingleTabAudit(): string {
    const auditType = this.getAuditType();
    switch (auditType) {
      case 'eeat-audit':
        return 'About 3–8 minutes typical for this audit';
      case 'landing-page-audit':
        return 'About 2–10 minutes typical (longer for multi-page sites)';
      case 'ai-audit':
        return 'About 3–12 minutes typical for this audit';
      default:
        return 'About 3–6 minutes typical';
    }
  }

  /**
   * Get displayed completed tabs count
   * Returns the actual completed tabs count (current tab in progress is not counted as completed)
   */
  getDisplayedCompletedTabs(): number {
    // Return the actual completed tabs count
    // The current tab is in progress, not completed, so it shouldn't be added to the count
    return this.completedTabs;
  }

  /**
   * Check if this is a landing page audit (to show "pages" instead of "tabs")
   */
  isLandingPageAudit(): boolean {
    const current = this.currentTab?.toLowerCase() || '';
    return current.includes('discovering pages') || 
           current.includes('auditing') && current.includes('pages') ||
           current.includes('landing-page-audit') ||
           current.includes('landing page audit');
  }

  /**
   * Check if we're in an audit context (any audit tab)
   */
  isAuditContext(): boolean {
    const auditKeywords = ['eeat-audit', 'landing-page-audit', 'ai-audit', 'eeat audit', 'landing page audit', 'ai audit', 'audit', 'auditing', 'page'];
    const current = this.currentTab?.toLowerCase() || '';
    
    // Check if current tab contains audit keywords
    if (auditKeywords.some(keyword => current.includes(keyword))) {
      return true;
    }
    
    // Check loaderState directly (before filtering) to see if any completed tab is an audit
    const allCompletedTabs = this.loaderState.completedTabNames || [];
    if (allCompletedTabs.some(tab => 
      auditKeywords.some(keyword => tab.toLowerCase().includes(keyword))
    )) {
      return true;
    }
    
    // For bulk operations with many tabs (like 55), assume audit tabs are included
    // This handles cases where we're processing multiple brands with audit tabs
    // 55 tabs likely means multiple brands × multiple tabs (including audit tabs)
    if (this.totalTabs > 20) {
      // Check if we see audit-related keywords in any tab name
      const allTabText = [...allCompletedTabs, current].join(' ').toLowerCase();
      if (auditKeywords.some(keyword => allTabText.includes(keyword))) {
        return true;
      }
      // For very high tab counts (bulk operations), assume audit tabs are included
      // and show "pages" by default
      if (this.totalTabs >= 30) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Get the unit label (tabs or pages).
   * Single-tab re-audits use totalTabs === 1 → always "tab"; bulk / landing discovery still use pages where relevant.
   */
  getUnitLabel(): string {
    if (this.totalTabs === 1) {
      return 'tab';
    }
    if (this.totalTabs === GlobalLoaderComponent.UNIFIED_BULK_STEP_COUNT) {
      return 'analysis steps';
    }
    return this.isLandingPageAudit() || this.isAuditContext() ? 'pages' : 'tabs';
  }

  get isOverviewStagesMode(): boolean {
    return !!this.loaderState.overviewStagesMode;
  }

  get overviewStageInfo(): { step: number; total: number; label: string } {
    const b = this.loaderState.overviewBackendStage;
    if (b && b.step >= 1 && b.total >= 1) {
      return { step: b.step, total: b.total, label: b.label || '…' };
    }
    return GenericTemplateCommonService.overviewLoaderStageFromPercent(this.progressPercentage);
  }

  /** Shown only when backend has not yet sent an ETA (e.g. before first WebSocket message) */
  get overviewTypicalTimeHint(): string {
    return '~2 min typical';
  }

  get showOverviewTypicalTimeHint(): boolean {
    return (
      this.isOverviewStagesMode &&
      (this.estimatedTimeRemaining == null || this.estimatedTimeRemaining <= 0)
    );
  }
}
